import { Briefcase } from '@emotion-icons/fa-solid/Briefcase'
import { capitalize, Typography } from '@mui/material'
import { AMOwnershipEntitySearchResult } from 'src/common-services'
import { Colors } from 'src/components/theme'
import { PARCEL_COLORS_MAP, PARCEL_TYPES_BY_MCODE_MAP } from 'src/constants/cherre-data-dictionary/zoning-colors'
import { formatString } from 'src/utils/format-string'
import { Chip } from 'src/v2/components/chip'
import { SidebarResultListItem } from 'src/v2/components/sidebar-result-list'
import { SUPPORTED_OWNERSHIP_IDS } from 'src/v2/types/owner-reveal'
import { OwnerSummary } from 'src/v2/views/owner-reveal/sidebar/owner-summary'
import { PropertyDistributionItem } from 'src/v2/views/owner-reveal/sidebar/result-overlay/properties-distribution-list'

export const getOwnerTypeLabel = (type: string): 'Unknown' | 'Individual' | 'Organization' => {
  if (!type) {
    return 'Unknown'
  }

  const lcType = type.toLowerCase()

  if (lcType === 'person') {
    return 'Individual'
  }

  return capitalize(lcType) as 'Organization'
}
type ParseEntityDistributionProps = {
  amountOfParcels: AMOwnershipEntitySearchResult['amountOfParcels']
  parcelsTypesDistribution: AMOwnershipEntitySearchResult['parcelsTypesDistribution']
}
export const parseEntityDistribution = (data: ParseEntityDistributionProps): Record<string, number> => {
  const distributions: Record<string, number> = {}
  const total = data.amountOfParcels
  data.parcelsTypesDistribution.forEach((item) => {
    const color = PARCEL_COLORS_MAP.get(item.mCode)
    if (!color) {
      return
    }

    const percentage = item.count / total

    if (distributions[color] !== undefined) {
      distributions[color] += percentage
    } else {
      distributions[color] = percentage
    }
  })
  return distributions
}

export const getPropertiesDistribution = (entity: AMOwnershipEntitySearchResult): PropertyDistributionItem[] => {
  if (!entity || !entity.parcelsTypesDistribution?.length) {
    return []
  }

  const groupByTypeMap: Record<string, number> = {}
  entity.parcelsTypesDistribution.forEach((item) => {
    const type = PARCEL_TYPES_BY_MCODE_MAP[item.mCode]

    if (groupByTypeMap[type]) {
      groupByTypeMap[type] += item.count
      return
    }
    groupByTypeMap[type] = item.count
  })

  const items = Object.entries(groupByTypeMap).map<PropertyDistributionItem>(([type, count]) => {
    const percentage = count / entity.amountOfParcels
    return {
      type,
      percentage,
      value: count,
    }
  })
  return items.sort((a, b) => b.percentage - a.percentage)
}

export const convertOwnerToSidebarResultListItem = (entity: AMOwnershipEntitySearchResult): SidebarResultListItem => {
  return {
    id: entity.id,
    title: formatString(entity.name, 'name'),
    subtitle: formatString(entity.address, 'address'),
    header: (
      <Chip icon={false} backgroundColor={Colors.neutral100}>
        <Typography variant="textmdm" color={Colors.neutral500}>
          {getOwnerTypeLabel(entity.type)}
        </Typography>
      </Chip>
    ),
    footer: (
      <OwnerSummary
        amountOfContacts={entity.amountOfContacts}
        amountOfParcels={entity.amountOfParcels}
        distributions={parseEntityDistribution(entity)}
      />
    ),
    icon: <Briefcase size={14} />,
    color: Colors.primaryRed800,
  }
}

export const getEntityLink = (entityId: string, includeDomain?: boolean): string => {
  const path = `/owner-reveal?id.${SUPPORTED_OWNERSHIP_IDS.ALPHAMAP_OWNERSHIP_ENITITY}=${entityId}`
  if (includeDomain) {
    return `${window.location.origin}${path}`
  }
  return path
}
export const getParcelLink = (parcelId: string, includeDomain?: boolean): string => {
  const path = `/owner-reveal?id.${SUPPORTED_OWNERSHIP_IDS.ALPHAMAP_OWNERSHIP_PARCEL}=${parcelId}`
  if (includeDomain) {
    return `${window.location.origin}${path}`
  }
  return path
}
